import React from 'react';
import { 
  MessageSquare, 
  Users, 
  BarChart3, 
  Settings, 
  Bot, 
  Home, 
  BookOpen, 
  Link2,
  Sparkles,
  PanelLeftClose,
  PanelLeftOpen,
  Store,
  ChevronRight,
  Zap,
  HelpCircle
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useSidebarStore } from './sidebarStore';
import { useNotificationStore } from '@/features/notifications/notificationStore';
import { useEcommerceIntegration } from '@/features/integrations/hooks/useIntegrations';
import { Tooltip } from '@/components/ui/tooltip';

interface NavItem {
  icon: React.ElementType;
  label: string;
  href: string;
  badge?: string | number;
  badgeColor?: string;
  category?: string;
}

const navSections: { title: string; items: NavItem[] }[] = [
  {
    title: 'Overview',
    items: [
      { icon: Home, label: 'Dashboard', href: '/dashboard' },
      { icon: MessageSquare, label: 'Conversations', href: '/conversations' },
      { icon: Users, label: 'Leads', href: '/leads' },
      { icon: BookOpen, label: 'Knowledge Base', href: '/knowledge' },
    ],
  },
  {
    title: 'AI & Growth',
    items: [
      { icon: Bot, label: 'Automations', href: '/automations' },
      { icon: Link2, label: 'Integrations', href: '/integrations' },
      { icon: BarChart3, label: 'Analytics', href: '/analytics' },
    ],
  },
  {
    title: 'Preferences',
    items: [
      { icon: Settings, label: 'Settings', href: '/settings' },
    ],
  },
];

export function Sidebar({ className }: { className?: string }) {
  const location = useLocation();
  const { isCollapsed, toggleCollapsed } = useSidebarStore();
  const { unreadCount } = useNotificationStore();
  const { connections: storeConnections } = useEcommerceIntegration();

  const activeStore = storeConnections.find(
    (c: any) => (c.platform === 'shopify' || c.platform === 'woocommerce') && c.isActive
  );

  return (
    <aside
      className={cn(
        'relative flex flex-col h-full bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-r border-slate-200/80 dark:border-slate-800 shadow-xs select-none transition-all duration-300 ease-in-out z-30',
        isCollapsed ? 'w-[72px]' : 'w-64',
        className
      )}
    >
      {/* Brand Header */}
      <div className={cn(
        'h-16 shrink-0 flex items-center border-b border-slate-100 dark:border-slate-800 transition-all duration-200',
        isCollapsed ? 'justify-center px-2' : 'justify-between px-4'
      )}>
        {!isCollapsed ? (
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-primary via-primary/90 to-indigo-600 text-white shadow-sm shadow-primary/20">
              <Bot className="h-5 w-5" />
              <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-400 ring-2 ring-white dark:ring-slate-900" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-bold text-slate-900 dark:text-slate-100 tracking-tight text-base leading-none">
                  Omnibiz
                </span>
                <span className="rounded-full bg-primary/10 px-1.5 py-0.2 text-[9px] font-bold text-primary tracking-wider uppercase">
                  PRO
                </span>
              </div>
              <p className="text-[10px] text-slate-400 dark:text-slate-500 font-medium tracking-tight mt-0.5 truncate">
                AI Automation Hub
              </p>
            </div>
          </div>
        ) : (
          <Tooltip content="Expand sidebar" placement="right" maxWidth="8rem">
            <button
              type="button"
              onClick={toggleCollapsed}
              className="relative flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-tr from-primary via-primary/90 to-indigo-600 text-white shadow-sm shadow-primary/20 transition-transform duration-200 hover:scale-105"
            >
              <Bot className="h-5 w-5" />
              <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-emerald-400 ring-2 ring-white dark:ring-slate-900" />
            </button>
          </Tooltip>
        )}

        {!isCollapsed && (
          <Tooltip content="Collapse sidebar" placement="bottom" maxWidth="8rem">
            <button
              type="button"
              onClick={toggleCollapsed}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              aria-label="Collapse sidebar"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </Tooltip>
        )}
      </div>

      {/* Active Workspace / Store Card (Expanded mode only) */}
      {!isCollapsed && (
        <div className="px-3 pt-3 pb-1">
          <Link
            to="/integrations"
            className="group flex items-center justify-between rounded-xl border border-slate-100 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-800/40 p-2 text-xs transition-all duration-150 hover:border-slate-200 dark:hover:border-slate-700 hover:bg-slate-100/80 dark:hover:bg-slate-800/80"
          >
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-emerald-100/70 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400">
                <Store className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-slate-800 dark:text-slate-200 truncate text-[11px] leading-tight">
                  {activeStore?.storeName || 'Shopify Store'}
                </p>
                <span className="flex items-center gap-1 text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  Live Sync
                </span>
              </div>
            </div>
            <ChevronRight className="h-3.5 w-3.5 text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
      )}

      {/* Nav List */}
      <nav className="flex-1 overflow-y-auto px-2.5 py-3 space-y-4">
        {navSections.map((section) => (
          <div key={section.title} className="space-y-1">
            {!isCollapsed && (
              <h4 className="px-2.5 text-[10px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                {section.title}
              </h4>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const isActive = location.pathname === item.href;
                const IconComponent = item.icon;
                const isConversation = item.href === '/conversations';
                const badgeCount = isConversation && unreadCount > 0 ? unreadCount : item.badge;

                const navLink = (
                  <Link
                    to={item.href}
                    className={cn(
                      'group relative flex items-center rounded-xl transition-all duration-150 font-medium text-xs',
                      isCollapsed
                        ? 'h-10 w-10 mx-auto justify-center'
                        : 'px-2.5 py-2 justify-between',
                      isActive
                        ? 'bg-primary/10 text-primary font-semibold shadow-2xs'
                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100/70 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-slate-100'
                    )}
                  >
                    {/* Left Active Accent Pill (Expanded mode) */}
                    {isActive && !isCollapsed && (
                      <span className="absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r-full bg-primary" />
                    )}

                    <div className={cn('flex items-center min-w-0', !isCollapsed && 'gap-2.5')}>
                      <IconComponent
                        className={cn(
                          'shrink-0 transition-transform duration-150 group-hover:scale-105',
                          isCollapsed ? 'h-5 w-5' : 'h-4 w-4',
                          isActive ? 'text-primary' : 'text-slate-500 dark:text-slate-400 group-hover:text-slate-800 dark:group-hover:text-slate-200'
                        )}
                      />
                      {!isCollapsed && (
                        <span className="truncate">{item.label}</span>
                      )}
                    </div>

                    {/* Badge or indicator */}
                    {!isCollapsed && badgeCount && (
                      <span
                        className={cn(
                          'rounded-full px-1.5 py-0.2 text-[10px] font-bold',
                          item.badgeColor || 'bg-primary text-white'
                        )}
                      >
                        {badgeCount}
                      </span>
                    )}

                    {/* Active dot in collapsed mode */}
                    {isCollapsed && isActive && (
                      <span className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-primary" />
                    )}
                  </Link>
                );

                if (isCollapsed) {
                  return (
                    <div key={item.label} className="flex justify-center py-0.5">
                      <Tooltip
                        content={
                          <div className="flex items-center gap-1.5">
                            <span className="font-semibold">{item.label}</span>
                            {badgeCount ? (
                              <span className="rounded bg-primary px-1 text-[9px] text-white">
                                {badgeCount}
                              </span>
                            ) : null}
                          </div>
                        }
                        placement="right"
                        maxWidth="10rem"
                      >
                        {navLink}
                      </Tooltip>
                    </div>
                  );
                }

                return <div key={item.label}>{navLink}</div>;
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer / Toggle trigger */}
      <div className="shrink-0 p-2.5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
        {isCollapsed ? (
          <div className="flex justify-center">
            <Tooltip content="Expand sidebar" placement="right" maxWidth="8rem">
              <button
                type="button"
                onClick={toggleCollapsed}
                className="flex h-9 w-9 items-center justify-center rounded-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 hover:bg-slate-200/60 dark:hover:bg-slate-800 transition-colors"
                aria-label="Expand sidebar"
              >
                <PanelLeftOpen className="h-4 w-4" />
              </button>
            </Tooltip>
          </div>
        ) : (
          <div className="flex items-center justify-between rounded-xl bg-white dark:bg-slate-850 border border-slate-200/70 dark:border-slate-800 p-2 shadow-2xs">
            <div className="flex items-center gap-2 min-w-0">
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600 dark:text-indigo-400">
                <Sparkles className="h-3.5 w-3.5" />
              </div>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold text-slate-800 dark:text-slate-200 leading-tight">AI Assistant</p>
                <p className="text-[10px] text-slate-400 dark:text-slate-500 font-medium leading-none mt-0.5">Automations Active</p>
              </div>
            </div>
            <button
              type="button"
              onClick={toggleCollapsed}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-slate-400 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              title="Collapse sidebar"
            >
              <PanelLeftClose className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}